import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/date-picker.css";
import { useFormContext, useWatch } from 'react-hook-form';
import moment from "moment-timezone";
import momentDate from "../../utils/moment-date";
import {DEFAULT_TIME_RANGES_FOR_REPORT_TYPE} from '../Constants'
import { isFalsy, shouldDateBeStartOfMonth, shouldDateBeStartOfMonthForBundle } from "../../utils/util";
import { DateInput } from "ra-ui-materialui";
import { showTimeRange, validateEndDate, validateStartDate } from "../../utils/CreateReportUtils";
import {Grid} from '@mui/material';
import { extractQueryParamsFromURL } from "../../mappers/Report";
import { useLocation } from 'react-router-dom';

const CustomDatePicker = (props) => {
  const { isBundleTemplate } = props; 
  const form = useFormContext();
  const values = useWatch();
  const location = useLocation();
  const [startDate, setStartDate] = React.useState();
  const [endDate, setEndDate] = React.useState();
  const [firstPeriodEndDate, setFirstPeriodEndDate] = React.useState(); //being used in case of report scheduling
  
  const firstPeriodEndDateFromUrl = extractQueryParamsFromURL(location?.search, 'firstPeriodEndDate');
  const resolvedFirstPeriodEndDate = firstPeriodEndDateFromUrl ? moment(firstPeriodEndDateFromUrl, 'DD/MM/YYYY').toDate() : new Date();  

  const handleStartDateChange = (val) => {
    setStartDate(val);
    form.setValue("startDate", momentDate(val).format("YYYY-MM-DD"), { shouldValidate: true });
  };

  const handleEndDateChange = (val) => {
    if (!isFalsy(val)) {
      setEndDate(val);
      form.setValue("endDate", momentDate(val).format("YYYY-MM-DD"), { shouldValidate: true });
    } else {
      setEndDate(undefined);
      form.setValue("endDate", undefined, { shouldValidate: true });
    }
  };

  const handlePeriodEndDateChange = (val) => {
    setFirstPeriodEndDate(val);
    form.setValue("firstPeriodEndDate", momentDate(val).format("YYYY-MM-DD"));
  };

  const handleDateChangeRaw = (e) => {
    e.preventDefault();
  };

  const isFirstOfMonth = (date) => {
    const currentDate = date.getDate();
    return currentDate === 1;
  };

useEffect(() => {//crs-hook
  const effectiveReportType = isBundleTemplate
    ? values.multiReportType?.find(reportType => showTimeRange(reportType))
    : values.reportType;

  const startDate = props.startDate 
    ? new Date(props.startDate) 
    : initialStartDate(effectiveReportType);

  if (!isFalsy(startDate)) {
    handleStartDateChange(startDate);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [props.startDate,
  values.reportType,
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  JSON.stringify(values.multiReportType), 
  isBundleTemplate]);

  useEffect(() => {//crs-hook
    const endDate = !isFalsy(props.endDate) ? new Date(props.endDate) : undefined;
    handleEndDateChange(endDate);    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[props.endDate]);

  useEffect(() => {
    setFirstPeriodEndDate(resolvedFirstPeriodEndDate)
    form.setValue("firstPeriodEndDate", momentDate(resolvedFirstPeriodEndDate).format("YYYY-MM-DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[values.frequency])

  return (
    <React.Fragment>
      {props.isStartDate && (                       //This hidden input's are used to invalidate form
        <Grid display="none">
          <DateInput
            source="startDate"
            defaultValue={startDate}
            validate={validateStartDate}
          />
        </Grid>
      )}
      {props.isEndDate && (
        <Grid display="none">
          <DateInput
            source="endDate"
            defaultValue={endDate}
            validate={validateEndDate}
          />
        </Grid>
      )}
      <Grid container>
        <Grid item xs={12} md={9}>
          <DatePicker
            id={props.isStartDate ? 
              "startDate-picker" 
              : (props.isEndDateOfFirstReport
                ? "endDateOfFirstReport-picker"
                : "endDate-picker")}
            popperPlacement="top"
            wrapperClassName="MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall"
            onChangeRaw={handleDateChangeRaw}
            onFocus={(ev) => {
              ev.preventDefault()
              ev.target.setAttribute('inputmode', 'none')
            }}
            popperModifiers={{
              flip: {
                behavior: ["top"], // don't allow it to flip to be above
              },
              preventOverflow: {
                enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
              },
              hide: {
                enabled: false, // turn off since needs preventOverflow to be enabled
              },
            }}
            selected={
              props.isStartDate === true
                ? startDate
                : (props.isEndDateOfFirstReport
                  ? firstPeriodEndDate
                  : endDate)
            }
            onChange={
              props.isStartDate === true
                ? handleStartDateChange
                : (props.isEndDateOfFirstReport
                  ? handlePeriodEndDateChange
                  : handleEndDateChange)
            }
            peekNextMonth
            maxDate={!props.isEndDateOfFirstReport ? new Date() : null}
            minDate={
              props.isEndDateOfFirstReport ? props.reportScheduleMinDate : null
            }
            showMonthDropdown
            showYearDropdown
            dateFormat="dd/MM/yyyy"
            dropdownMode="select"
            filterDate={
              shouldDateBeStartOfMonthForBundle(values.reportType, values.multiReportType) &&
              props.isStartDate === true
                ? isFirstOfMonth
                : null
            }
            isClearable={props.isEndDate}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default CustomDatePicker;

export const initialStartDate = (reportType) => {
  if (!reportType) return;

  // removing this default start of 3 months , as when component was rendered for first time and reportType was not selected
  //it was defaulted to 3 months and validation error used to come even when SOFA report had 6 months selected
  let startDate = moment().subtract(
    DEFAULT_TIME_RANGES_FOR_REPORT_TYPE[reportType],
    "months"
  );

  const date = new Date(startDate);
  if (
    (shouldDateBeStartOfMonth(reportType)) &&
    date.getDate() !== 1
  ) {
    date.setDate(1);
    return date;
  }
  return startDate.toDate(); //react datepicker expects initial date in javascript date format only
};

